import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Segmented, Spin, Tag, Typography } from 'antd';
import QRCode from 'qrcode';
import {
  useConfirmTwoFactor,
  useDisableTwoFactor,
  useEnableTwoFactor,
  useTwoFactorStatus,
} from '@presentation/hooks/useTwoFactor';

type Step = 'idle' | 'setup' | 'recovery-codes';
type SetupMethod = 'qrcode' | 'secret';

/** Renders the otpauth:// provisioning URI as a scannable QR code image. */
const TwoFactorQrCode = ({ qrCodeUrl }: { qrCodeUrl: string }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(qrCodeUrl, { width: 220, margin: 1 })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled)
          setError('Could not render the QR code — enter the secret manually instead.');
      });
    return () => {
      cancelled = true;
    };
  }, [qrCodeUrl]);

  if (error) return <Alert type="warning" showIcon title={error} />;
  if (!dataUrl) {
    return (
      <div className="flex h-[236px] w-[236px] items-center justify-center rounded border border-black/10 dark:border-white/10">
        <Spin size="small" />
      </div>
    );
  }
  return (
    <img
      src={dataUrl}
      alt="Two-factor authentication QR code"
      width={220}
      height={220}
      className="rounded border border-black/10 dark:border-white/10"
    />
  );
};

export const TwoFactorSetup = () => {
  const { data: status, isLoading } = useTwoFactorStatus();
  const enable = useEnableTwoFactor();
  const confirm = useConfirmTwoFactor();
  const disable = useDisableTwoFactor();

  const [step, setStep] = useState<Step>('idle');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [setupMethod, setSetupMethod] = useState<SetupMethod>('qrcode');

  const handleStart = () => {
    setSetupMethod('qrcode');
    enable.mutate(undefined, { onSuccess: () => setStep('setup') });
  };

  const handleConfirm = ({ code }: { code: string }) => {
    confirm.mutate(code, {
      onSuccess: (codes) => {
        setRecoveryCodes(codes);
        setStep('recovery-codes');
      },
    });
  };

  const handleDisable = ({ password }: { password: string }) => {
    disable.mutate(password, { onSuccess: () => setStep('idle') });
  };

  if (isLoading) {
    return <Spin />;
  }

  if (step === 'recovery-codes') {
    return (
      <Alert
        type="success"
        showIcon
        title="Save your recovery codes"
        description={
          <div className="space-y-3">
            <Typography.Text>
              Store these somewhere safe. Each code can be used once if you lose access to your
              authenticator app.
            </Typography.Text>
            <ul className="grid grid-cols-2 gap-2">
              {recoveryCodes.map((recoveryCode) => (
                <li key={recoveryCode}>
                  <Typography.Text code>{recoveryCode}</Typography.Text>
                </li>
              ))}
            </ul>
            <Button onClick={() => setStep('idle')}>Done</Button>
          </div>
        }
        className="max-w-md"
      />
    );
  }

  if (step === 'setup' && enable.data) {
    return (
      <Form layout="vertical" className="max-w-md" onFinish={handleConfirm}>
        <Typography.Paragraph type="secondary">
          Set up with your authenticator app by scanning a QR code or entering the secret manually.
        </Typography.Paragraph>

        <Segmented
          value={setupMethod}
          onChange={(value) => setSetupMethod(value as SetupMethod)}
          options={[
            { label: 'QR code', value: 'qrcode' },
            { label: 'Enter secret', value: 'secret' },
          ]}
          className="mb-3"
        />

        {setupMethod === 'qrcode' ? (
          <div className="mb-3 flex justify-center">
            <TwoFactorQrCode qrCodeUrl={enable.data.qrCodeUrl} />
          </div>
        ) : (
          <Typography.Paragraph>
            <Typography.Text code copyable>
              {enable.data.secret}
            </Typography.Text>
          </Typography.Paragraph>
        )}

        <Form.Item
          name="code"
          label="6-digit code"
          rules={[{ required: true, message: 'Code is required' }]}
        >
          <Input autoFocus />
        </Form.Item>

        {confirm.isError && (
          <Alert type="error" showIcon title={confirm.error.message} className="mb-4" />
        )}

        <div className="flex gap-2">
          <Button type="primary" htmlType="submit" loading={confirm.isPending}>
            Confirm
          </Button>
          <Button onClick={() => setStep('idle')}>Cancel</Button>
        </div>
      </Form>
    );
  }

  if (status?.enabled) {
    return (
      <Form layout="vertical" className="max-w-md" onFinish={handleDisable}>
        <Tag color="success" className="mb-4">
          Two-factor authentication is enabled
        </Tag>
        <Form.Item
          name="password"
          label="Confirm your password to disable"
          rules={[{ required: true, message: 'Password is required' }]}
        >
          <Input.Password />
        </Form.Item>
        {disable.isError && (
          <Alert type="error" showIcon title={disable.error.message} className="mb-4" />
        )}
        <Button danger htmlType="submit" loading={disable.isPending}>
          Disable two-factor authentication
        </Button>
      </Form>
    );
  }

  return (
    <div className="max-w-md space-y-3">
      <Typography.Text type="secondary">
        Two-factor authentication is disabled. Enable it for an extra layer of security.
      </Typography.Text>
      <div>
        <Button type="primary" onClick={handleStart} loading={enable.isPending}>
          Enable two-factor authentication
        </Button>
      </div>
    </div>
  );
};
